import axios, { AxiosResponse } from 'axios'
import { v4 as uuidv4 } from 'uuid'

import { EPathSegment } from '../utils'
import { CLIENT_ID_HEADER, CLIENT_ID_HEADER_VALUE, CUSTOM_AUTH_HEADER, ELoginState } from '../utils/consts'

import ELocale from '../types/locales'
import { TSuggestElement } from '../types/factFinder'
import { ECountryCode } from '../components/CountrySelector/CountrySelector'

import { RefreshTokenConsumer } from '@hse24/shared-utilities/refresh-token'
import { normalizeSearchTerm } from '../utils/normalizeSearchTerm'

const useRefreshToken = new RefreshTokenConsumer(EPathSegment.REFRESH_TOKEN)

export default {
  /*
    usage CLIENT ONLY : SearchBar updateSuggestions < React.useEffect
  */
  async getSuggestions(searchTerm: string, locale: ELocale): Promise<AxiosResponse<{ data: TSuggestElement[] }>> {
    const normalizedSearchTerm = normalizeSearchTerm(searchTerm)
    return await axios.get(
      getUrl(`${EPathSegment.SUGGEST}${mapLocaleToDomain(locale)}?query=${encodeURIComponent(normalizedSearchTerm)}`),
      {
        headers: { [CLIENT_ID_HEADER]: CLIENT_ID_HEADER_VALUE },
      },
    )
  },

  /*
    usage CLIENT ONLY : getBasketItemCount < Header.tsx updateCartCount < useEffect (imported from react)
      window.eev.on('update-cart-count', () => {
        updateCartCount()
      })
    in pdp-view AddToBasket.tsx: window.eev.emit('update-cart-count')
  */
  async getAuthToken(identityId: string): Promise<AxiosResponse<{ Token: string }>> {
    return await axios.post(
      'https://cognito-identity.eu-central-1.amazonaws.com/',
      { IdentityId: identityId },
      {
        headers: {
          'X-Amz-Target': 'AWSCognitoIdentityService.GetOpenIdToken',
          'Content-Type': 'application/x-amz-json-1.1',
        },
      },
    )
  },

  /*
      usage CLIENT ONLY : Header.tsx updateCartCount < useEffect (imported from react)
        window.eev.on('update-cart-count', () => {
          updateCartCount()
        })
      in pdp-view AddToBasket.tsx: window.eev.emit('update-cart-count')
  */
  async getBasketItemCount(bearer: string, urlOverwrite?: string): Promise<AxiosResponse> {
    return await axios.head(urlOverwrite || getUrl(EPathSegment.BASKET), {
      headers: {
        [CUSTOM_AUTH_HEADER]: bearer,
        [CLIENT_ID_HEADER]: CLIENT_ID_HEADER_VALUE,
      },
    })
  },

  /*
    usage CLIENT ONLY : Header.tsx updateLoginState < useEffect (imported from react)
    window.eev.on('update-login-state', () => {
      updateLoginState()
    })
    updateLoginState()
  */
  async getLoginState(): Promise<AxiosResponse<{ state?: ELoginState }>> {
    return useRefreshToken.wrap(
      () =>
        axios.get(getUrl(EPathSegment.LOGIN_STATE), {
          withCredentials: true,
          headers: {
            Accept: 'application/json',
            [CLIENT_ID_HEADER]: CLIENT_ID_HEADER_VALUE,
          },
        }),
      { doLoginStateCheck: true },
    )
  },

  /*
    usage CLIENT ONLY : CountrySelector < UseShowModal useEffect
   */
  async getCountryCode(): Promise<AxiosResponse<{ country?: ECountryCode | null }>> {
    return await axios.get(getUrl(EPathSegment.COUNTRY_CODE), {
      headers: { [CLIENT_ID_HEADER]: CLIENT_ID_HEADER_VALUE },
    })
  },

  /*
    usage CLIENT ONLY: SignUpForm React.useEffect
  */
  createNewsletterSubscription: (email: string): Promise<AxiosResponse> => {
    return axios.post(
      getUrl(EPathSegment.NEWSLETTER_SIGNUP),
      { email },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-CorrelationId': uuidv4(),
          [CLIENT_ID_HEADER]: CLIENT_ID_HEADER_VALUE,
        },
      },
    )
  },
}

export const mapLocaleToDomain = (locale: ELocale | undefined): string => {
  return {
    [ELocale.DE_DE]: 'de',
    [ELocale.DE_AT]: 'at',
    [ELocale.DE_CH]: 'ch',
  }[locale || ELocale.DE_DE]
}

export const getUrl = (url: string): string => {
  if (process.env.NODE_ENV === 'pact') {
    return url.replace('BASE_URL_DEVELOPMENT', 'http://localhost:1234')
  }
  return url
}
